/*
 * Pen PDP inline configurator.
 *
 * BUSINESS-LOGIC BRIDGE
 * This component is presentation only. It filters the established
 * window.__touchupSizeMap to Pen entries, then mirrors the chosen color,
 * format, and package into the theme's hidden touch-up controls. The native
 * touch-up engine continues to own variant resolution, pricing, validation,
 * VIN line-item properties, quantity confirmation, and cart submission.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-tupp-configurator]';
  var CONTENT_SELECTOR = '[data-tupp-configurator-content]';
  var DATA_RETRY_LIMIT = 48;
  var DATA_RETRY_DELAY = 250;

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  function normalizeVin(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .replace(/[IOQ]/g, '')
      .slice(0, 17);
  }

  function normalizePlate(value) {
    return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
  }

  function formatMoney(value) {
    var amount = Number(String(value == null ? '' : value).replace(/,/g, ''));
    return isNaN(amount) ? '' : '$' + amount.toFixed(2);
  }

  function isVinMatch(code) {
    return /^(?:\.vin|vin)$|match\s+(?:paint\s+)?by\s+vin/i.test(String(code || '').trim());
  }

  function boot(root) {
    if (!root || root.__tuppConfiguratorBooted) return;
    root.__tuppConfiguratorBooted = true;

    var content = root.querySelector(CONTENT_SELECTOR);
    if (!content) return;

    var packageImages = {
      paintonly: root.getAttribute('data-tupp-paint-only-image') || '',
      essential: root.getAttribute('data-tupp-essential-image') || '',
      preferred: root.getAttribute('data-tupp-preferred-image') || '',
      complete: root.getAttribute('data-tupp-complete-image') || '',
    };
    var state = {
      method: 'vin',
      colorCode: '',
      colorDetails: null,
      selectedPackage: '',
      vin: '',
      plate: '',
      plateState: '',
      plateMessage: '',
      resolvingPlate: false,
      resolvingVin: false,
      resolvedVin: '',
      userChoseMethod: false,
      vehicleKey: '',
      allowedCodes: null,
      allowedCodesLoading: false,
      paintDataAvailable: null,
      twoToneCodes: [],
      toneChoice: '',
      toneArea: '',
      manualVinSaved: false,
      adding: false,
    };
    var renderTimer = 0;
    var dataRetryTimer = 0;
    var paintContextRequest = 0;

    function getMap() {
      return window.__touchupSizeMap && typeof window.__touchupSizeMap === 'object'
        ? window.__touchupSizeMap
        : {};
    }

    function normalizePaintCode(value) {
      return String(value || '').trim().toUpperCase();
    }

    function comparablePaintCode(value) {
      var code = normalizePaintCode(value);
      return code.indexOf('WA') === 0 ? code.slice(2) : code;
    }

    function paintCodeMatches(code, allowed) {
      var comparable = comparablePaintCode(code);
      var approved = comparablePaintCode(allowed);
      return comparable === approved || comparable.indexOf(approved + ' ') === 0 || comparable.indexOf(approved + '+') === 0;
    }

    function uniquePaintCodes(codes) {
      return (Array.isArray(codes) ? codes : []).map(normalizePaintCode).filter(function (code, index, all) {
        return code && !isVinMatch(code) && all.indexOf(code) === index;
      });
    }

    function codeForMethod() {
      if (state.method !== 'vin') return state.colorCode;
      var exactCode = resolvedVehiclePaintCode();
      if (exactCode) return exactCode;
      return Object.keys(getMap()).find(isVinMatch) || '';
    }

    function mapKeyForCode(code) {
      var normalizedCode = String(code || '').trim().toUpperCase();
      return Object.keys(getMap()).find(function (key) {
        return String(key || '').trim().toUpperCase() === normalizedCode;
      }) || '';
    }

    function colorOption(code) {
      var select = document.getElementById('variant-selector');
      if (!select) return null;
      return Array.prototype.slice.call(select.options).find(function (option) {
        var title = option.getAttribute('data-variant-title') || option.textContent || '';
        return String(title).trim().toUpperCase() === String(code || '').trim().toUpperCase();
      }) || null;
    }

    function colorLabel(code) {
      var colors = window.PartifyTouchupColorDetails;
      if (colors && activeVehicle()) return colors.label(state.colorDetails, code);
      var option = colorOption(code);
      var label = option ? String(option.textContent || '').split('+')[0].trim() : '';
      return label && label.toUpperCase() !== String(code || '').toUpperCase()
        ? code + ' - ' + label
        : code;
    }

    function penEntries() {
      var requestedCode = codeForMethod();
      var code = isVinMatch(requestedCode) ? requestedCode : mapKeyForCode(requestedCode);
      return (getMap()[code] || []).filter(function (entry) {
        return entry && entry.id && String(entry.size || '').trim().toLowerCase() === 'pen';
      });
    }

    function orderedPackages() {
      var preferredOrder = Array.isArray(window.__touchupPackageOrder)
        ? window.__touchupPackageOrder.slice()
        : [];
      var fallbackOrder = ['Paint Only', 'Essential', 'Preferred', 'Complete'];
      fallbackOrder.forEach(function (name) {
        if (preferredOrder.indexOf(name) === -1) preferredOrder.push(name);
      });

      var names = penEntries().map(function (entry) { return String(entry.packageName || '').trim(); });
      var unique = names.filter(function (name, index) { return name && names.indexOf(name) === index; });
      return unique.sort(function (left, right) {
        var leftIndex = preferredOrder.indexOf(left);
        var rightIndex = preferredOrder.indexOf(right);
        if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
        if (leftIndex === -1) return 1;
        if (rightIndex === -1) return -1;
        return leftIndex - rightIndex;
      });
    }

    function packageMeta(name) {
      var meta = {
        paintonly: {
          description: 'Factory-matched color for pinpoint chips and fine scratches.',
          inclusions: ['Matched color pen'],
          note: 'For prepared repairs that do not need clearcoat',
        },
        essential: {
          description: 'Matched color plus the clear protection most finished repairs need.',
          inclusions: ['Matched color pen', 'Clearcoat pen'],
          note: 'Recommended for a sealed finish',
        },
        preferred: {
          description: 'Adds primer for chips that break through the finish.',
          inclusions: ['Primer pen', 'Matched color pen', 'Clearcoat pen'],
          note: 'For exposed primer or bare metal',
        },
        complete: {
          description: 'The full mapped pen system for a more involved precision repair.',
          inclusions: ['Primer pen', 'Matched color pen', 'Clearcoat pen'],
          note: 'Shown only when a complete pen set is mapped to your color',
        },
      };
      return meta[normalize(name)] || {
        description: 'A factory-matched precision pen package.',
        inclusions: ['Factory-matched pen'],
        note: '',
      };
    }

    function entryForPackage(name) {
      return penEntries().find(function (entry) {
        return String(entry.packageName || '') === String(name || '');
      }) || null;
    }

    function selectedPackage() {
      var packages = orderedPackages();
      if (packages.indexOf(state.selectedPackage) !== -1) return state.selectedPackage;
      return packages.find(function (name) { return normalize(name) === 'essential'; }) || packages[0] || '';
    }

    function activeVehicle() {
      try {
        var list = typeof window.getSearchTerms === 'function'
          ? window.getSearchTerms()
          : JSON.parse(localStorage.getItem('searchTerms') || '[]');
        return Array.isArray(list) && list.length ? list[0] : null;
      } catch (error) {
        return null;
      }
    }

    function activeVinString() {
      var vehicle = activeVehicle() || {};
      var manualInput = root.querySelector('[data-tupp-manual-vin]');
      return normalizeVin(
        vehicle.vin || vehicle.VIN || vehicle.vehicleVin || vehicle.vehicleVIN ||
        vehicle.vinNumber || vehicle.vin_string || vehicle.vinString ||
        vehicle.decodedVin || vehicle.decodedVIN || (manualInput && manualInput.value) || state.vin || ''
      );
    }

    function syncManualVinProperty() {
      var vin = activeVinString();
      Array.prototype.slice.call(document.querySelectorAll('#product-form, form.product-form, form[action*="/cart/add"]')).forEach(function (form) {
        if (!form || vin.length !== 17) return;
        var input = form.querySelector('input[name="properties[VIN]"]') || getOrCreateHiddenInput(form, 'input[name="properties[VIN]"]', 'properties[VIN]');
        input.value = vin;
      });
    }

    function resolvedVehiclePaintCode() {
      var vehicle = activeVehicle() || {};
      var finderData = window.PartifyTouchupFinderData || {};
      var raw = typeof finderData.getResolvedPaintCode === 'function'
        ? finderData.getResolvedPaintCode(vehicle)
        : vehicle.paintCode || vehicle.paint_code || vehicle.troublesome_format || '';
      raw = String(raw || '').trim();
      if (raw.indexOf(' / ') !== -1) raw = raw.split(' / ').pop().trim();
      if (!raw || isVinMatch(raw) || normalizeVin(raw) === activeVinString()) return '';
      return mapKeyForCode(raw);
    }

    function localTwoToneCodes(vehicle) {
      var config = window.__PARTIFY_TWO_TONE_PAINT_RULES;
      var resolver = window.PartifyTwoToneRuleResolver;
      if (!config || !Array.isArray(config.rules) || !resolver || typeof resolver.selectRule !== 'function') return [];
      var selected = resolver.selectRule(config.rules, vehicle || {});
      return selected && Array.isArray(selected.codes) ? selected.codes : [];
    }

    function loadVehiclePaintContext(vehicle) {
      var request = ++paintContextRequest;
      state.colorDetails = null;
      if (!vehicle) {
        state.allowedCodes = null;
        state.allowedCodesLoading = false;
        state.paintDataAvailable = null;
        state.twoToneCodes = [];
        state.toneChoice = '';
        state.toneArea = '';
        scheduleRender();
        return Promise.resolve();
      }

      state.allowedCodes = null;
      state.allowedCodesLoading = true;
      state.paintDataAvailable = null;
      scheduleRender();
      var finderData = window.PartifyTouchupFinderData || {};
      if (typeof finderData.getColorDetails === 'function') {
        Promise.resolve(finderData.getColorDetails(vehicle)).then(function (details) {
          if (request !== paintContextRequest) return;
          state.colorDetails = details;
          var colors = window.PartifyTouchupColorDetails;
          if (colors) {
            colors.updateOptions(content.querySelector('[data-tupp-color]'), details);
            colors.renderPreview(content.querySelector('[data-touchup-color-preview]'), details, state.colorCode);
          }
        }).catch(function () {});
      }
      var kitPromise = typeof finderData.ensureKitProducts === 'function'
        ? Promise.resolve(finderData.ensureKitProducts(vehicle)).catch(function () { return false; })
        : Promise.resolve(false);
      var allowedPromise = typeof finderData.getAllowedCodes === 'function'
        ? finderData.getAllowedCodes(vehicle)
        : typeof window.__partifyTouchupFetchAllowedCodes === 'function'
          ? window.__partifyTouchupFetchAllowedCodes(vehicle)
          : Promise.resolve(null);
      var twoTonePromise = typeof finderData.getTwoToneCodes === 'function'
        ? finderData.getTwoToneCodes(vehicle)
        : Promise.resolve(localTwoToneCodes(vehicle));

      return kitPromise.then(function () {
        return Promise.all([allowedPromise, twoTonePromise]);
      }).then(function (results) {
        if (request !== paintContextRequest) return;
        state.paintDataAvailable = Array.isArray(results[0]) && (results[0].partifyVehiclePaintDataAvailable === true || results[0].length > 0);
        state.allowedCodes = Array.isArray(results[0]) ? uniquePaintCodes(results[0]) : null;
        state.allowedCodesLoading = false;
        state.twoToneCodes = uniquePaintCodes(results[1]);
        if (!state.twoToneCodes.length) {
          state.toneChoice = '';
          state.toneArea = '';
        }
        if (state.method === 'manual' && state.colorCode && manualCodes().indexOf(state.colorCode) === -1) {
          state.colorCode = '';
          state.selectedPackage = '';
        }
        scheduleRender();
      }).catch(function () {
        if (request !== paintContextRequest) return;
        state.allowedCodes = null;
        state.allowedCodesLoading = false;
        state.paintDataAvailable = false;
        state.twoToneCodes = uniquePaintCodes(localTwoToneCodes(vehicle));
        scheduleRender();
      });
    }

    function twoToneEligible() {
      return state.twoToneCodes.length > 0;
    }

    function secondaryToneSelected() {
      return twoToneEligible() && state.toneChoice === 'two' && state.toneArea === 'secondary';
    }

    function toneSelectionReady() {
      if (!twoToneEligible()) return true;
      if (state.toneChoice === 'one') return true;
      return state.toneChoice === 'two' && (state.toneArea === 'primary' || state.toneArea === 'secondary');
    }

    function vinPaintDataAvailable() {
      return state.paintDataAvailable === true;
    }

    function vinAvailabilityStatus() {
      if (state.method !== 'vin' || state.vin.length !== 17 || state.resolvedVin !== state.vin) return '';
      if (state.allowedCodesLoading) return 'Checking Shopify paint availability for this vehicle…';
      if (!vinPaintDataAvailable()) {
        return 'Touch-up paint is not available for this vehicle because we do not have a Shopify paint-code mapping for it. Match by VIN and Add to Cart are disabled.';
      }
      return '';
    }

    function vinResultMarkup() {
      if (state.method !== 'vin' || state.vin.length !== 17 || state.resolvedVin !== state.vin || state.allowedCodesLoading || !vinPaintDataAvailable()) return '';
      var code = codeForMethod();
      if (!code) return '';
      var label = isVinMatch(code) ? 'Match by VIN' : code;
      return '<p class="tupp-inline-configurator__vin-result" data-tupp-vin-result role="status"><span>VIN: ' + escapeHtml(activeVinString()) + '</span><span>Paint code: ' + escapeHtml(label) + '</span></p>';
    }

    function vinDecodeSuccessMessage(vehicle) {
      var finderData = window.PartifyTouchupFinderData || {};
      var code = typeof finderData.getResolvedPaintCode === 'function'
        ? finderData.getResolvedPaintCode(vehicle || activeVehicle())
        : (vehicle && (vehicle.paintCode || vehicle.paint_code || vehicle.troublesome_format)) || '';
      code = String(code || '').trim();
      if (code && !isVinMatch(code)) {
        return 'VIN decoded successfully. Your vehicle was added to Garage. Paint code ' + code.toUpperCase() + ' was found.';
      }
      return 'VIN decoded successfully. Your vehicle was added to Garage. We will match the paint by VIN.';
    }

    function manualCodes() {
      var codes = Object.keys(getMap()).filter(function (code) { return !isVinMatch(code); }).sort();
      var filter = secondaryToneSelected() ? state.twoToneCodes : state.allowedCodes;
      if (!Array.isArray(filter)) return codes;
      return codes.filter(function (code) {
        return filter.some(function (allowed) { return paintCodeMatches(code, allowed); });
      });
    }

    function vehicleLabel(vehicle) {
      if (!vehicle) return '';
      return String(
        vehicle.ymm ||
        vehicle.YMM ||
        vehicle.vehicle ||
        [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel].filter(Boolean).join(' ')
      ).trim();
    }

    function openGarage() {
      if (window.GarageV2 && typeof window.GarageV2.open === 'function') {
        window.GarageV2.open();
        return;
      }
      if (typeof window.toggleGaragePopup === 'function') {
        window.toggleGaragePopup();
        return;
      }
      var button = document.getElementById('easyBtn') || document.getElementById('easyBtnMobile');
      if (button && typeof button.click === 'function') button.click();
    }

    function garageMarkup(vehicle) {
      var label = vehicleLabel(vehicle);
      if (!label) {
        return '<button type="button" class="tupp-inline-configurator__garage tupp-inline-configurator__garage--empty" data-tupp-garage>' +
          '<span class="tupp-inline-configurator__garage-icon" aria-hidden="true">+</span>' +
          '<span><strong>Add a vehicle to your garage</strong><small>Choose it once, then keep it ready for future color-matched repairs.</small></span>' +
          '<span aria-hidden="true">&rarr;</span>' +
        '</button>';
      }
      return '<div class="tupp-inline-configurator__garage is-saved">' +
        '<span class="tupp-inline-configurator__garage-icon" aria-hidden="true">&#10003;</span>' +
        '<span><small>Shopping for</small><strong>' + escapeHtml(label) + '</strong></span>' +
        '<button type="button" data-tupp-garage>Change</button>' +
      '</div>';
    }

    function getOrCreateHiddenInput(form, selector, name) {
      var input = form.querySelector(selector);
      if (input) return input;
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
      return input;
    }

    function clickNativeTouchupButton(containerId, label) {
      var buttons = Array.prototype.slice.call(
        document.querySelectorAll('#' + containerId + ' .touchup-option-button')
      );
      var button = buttons.find(function (item) {
        return String(item.textContent || '').trim().toLowerCase() === String(label || '').trim().toLowerCase();
      });
      if (button && !button.disabled) button.click();
    }

    function syncExistingControls() {
      var code = codeForMethod();
      var activeVin = activeVinString();
      var matchByVinRequired = state.method === 'vin' && isVinMatch(code);
      var packageName = selectedPackage();
      var entry = entryForPackage(packageName);
      if (!code || !entry || !entry.id) return false;

      var select = document.getElementById('variant-selector');
      var option = colorOption(code);
      if (select && option) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      window.setTimeout(function () {
        clickNativeTouchupButton('touchup-size-buttons', 'Pen');
        clickNativeTouchupButton('touchup-package-buttons', packageName);
      }, 0);

      window.__touchupSelectedKitEntry = entry;
      window.__touchupSelectedKitVariantId = String(entry.id);
      window.__touchupSelectedKitSku = entry.sku || '';

      var forms = Array.prototype.slice.call(document.querySelectorAll(
        '#product-form, form.product-form, form[action*="/cart/add"]'
      ));
      forms.forEach(function (form) {
        if (!form) return;
        var kitInput = getOrCreateHiddenInput(
          form,
          'input[data-touchup-kit-variant-id]',
          'id'
        );
        kitInput.setAttribute('data-touchup-kit-variant-id', 'true');
        kitInput.value = String(entry.id);

        var vinInput = form.querySelector('input[name="properties[VIN]"]');
        if (activeVin.length === 17) {
          vinInput = vinInput || getOrCreateHiddenInput(form, 'input[name="properties[VIN]"]', 'properties[VIN]');
          vinInput.value = activeVin;
        } else if (vinInput) {
          vinInput.value = '';
        }
      });

      if (matchByVinRequired && activeVin.length === 17) {
        window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = true;
        window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_MISSING = false;
        window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = false;
      } else {
        window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = false;
        window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_MISSING = false;
        window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = state.method === 'manual';
      }

      return true;
    }

    function methodReady() {
      if (!toneSelectionReady()) return false;
      if (state.method === 'vin') return state.vin.length === 17 && state.resolvedVin === state.vin && !state.resolvingVin && !state.allowedCodesLoading && vinPaintDataAvailable() && !!codeForMethod();
      if (state.method === 'plate') return state.plate.length >= 2 && !!state.plateState;
      return !!state.colorCode;
    }

    function setMethod(method) {
      if (secondaryToneSelected() && method !== 'manual') method = 'manual';
      state.method = method;
      state.userChoseMethod = true;
      state.plateMessage = '';
      if (method !== 'manual') state.colorCode = '';
      window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = method === 'manual';
      window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = false;
      state.selectedPackage = '';
      scheduleRender();
    }

    function renderToneFields() {
      if (!twoToneEligible()) return '';
      var choices = [
        { value: 'one', title: 'One factory color', copy: 'Use VIN, plate, or the vehicle\'s factory paint-code list.' },
        { value: 'two', title: 'Two factory colors', copy: 'Choose the main or accent color you need to repair.' },
      ];
      var markup = '<fieldset class="tupp-tone-choice"><legend>How many factory paint colors does your vehicle have?</legend><div class="tupp-tone-choice__grid">' + choices.map(function (choice) {
        var selected = state.toneChoice === choice.value;
        return '<button type="button" class="tupp-tone-option' + (selected ? ' is-selected' : '') + '" data-tupp-tone="' + choice.value + '" aria-pressed="' + String(selected) + '"><strong>' + choice.title + '</strong><span>' + choice.copy + '</span></button>';
      }).join('') + '</div></fieldset>';
      if (state.toneChoice === 'two') {
        markup += '<fieldset class="tupp-tone-choice"><legend>Which color are you touching up?</legend><div class="tupp-tone-choice__grid">' + [
          { value: 'primary', title: 'Main / primary color', copy: 'The color covering most of the vehicle.' },
          { value: 'secondary', title: 'Accent / secondary color', copy: 'The contrasting second factory color.' },
        ].map(function (choice) {
          var selected = state.toneArea === choice.value;
          return '<button type="button" class="tupp-tone-option' + (selected ? ' is-selected' : '') + '" data-tupp-tone-area="' + choice.value + '" aria-pressed="' + String(selected) + '"><strong>' + choice.title + '</strong><span>' + choice.copy + '</span></button>';
        }).join('') + '</div></fieldset>';
      }
      return markup;
    }

    function renderMethodFields() {
      if (state.method === 'vin') {
        return '<label class="tupp-inline-configurator__field"><span>Vehicle identification number</span>' +
          '<input data-tupp-vin value="' + escapeAttribute(state.vin) + '" maxlength="17" inputmode="text" autocomplete="off" placeholder="17-character VIN"></label>';
      }

      if (state.method === 'plate') {
        var stateCodes = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
        return '<div class="tupp-inline-configurator__field-row">' +
          '<label class="tupp-inline-configurator__field"><span>License plate</span><input data-tupp-plate value="' + escapeAttribute(state.plate) + '" autocomplete="off" placeholder="ABC1234"></label>' +
          '<label class="tupp-inline-configurator__field"><span>State</span><select data-tupp-plate-state><option value="">State</option>' +
          stateCodes.map(function (code) {
            return '<option value="' + code + '"' + (state.plateState === code ? ' selected' : '') + '>' + code + '</option>';
          }).join('') + '</select></label></div>' +
          '<p class="tupp-inline-configurator__helper">We use the plate and state to find the VIN, then match the factory color.</p>' +
          (state.plateMessage ? '<p class="tupp-inline-configurator__status">' + escapeHtml(state.plateMessage) + '</p>' : '');
      }

      var codes = manualCodes();
      var placeholder = state.allowedCodesLoading ? 'Loading paint codes…' : codes.length ? 'Select factory paint code' : 'No paint codes found for this vehicle';
      var options = ['<option value="">' + placeholder + '</option>'].concat(codes.map(function (code) {
        return '<option value="' + escapeAttribute(code) + '"' + (code === state.colorCode ? ' selected' : '') + '>' + escapeHtml(colorLabel(code)) + '</option>';
      }));
      return '<label class="tupp-inline-configurator__field"><span>Factory paint code</span><select data-tupp-color' + (state.allowedCodesLoading || !codes.length ? ' disabled' : '') + '>' + options.join('') + '</select></label>' +
        '<figure class="touchup-color-preview" data-touchup-color-preview hidden></figure>' +
        '<p class="tupp-inline-configurator__helper">' + (secondaryToneSelected() ? 'Choose the factory code for the accent / secondary color you are repairing.' : 'Paint codes are limited to the selected vehicle. Find yours on the driver-side door jamb.') + '</p>' +
        (state.colorCode ? manualVinRecommendationMarkup() : '');
    }

    function manualVinRecommendationMarkup() {
      var vin = activeVinString();
      return '<div class="tupp-inline-configurator__manual-vin" data-tupp-manual-vin-prompt>' +
        '<strong>Add VIN for Better Color Match <em>(Recommended)</em></strong>' +
        '<label class="tupp-inline-configurator__field"><span>Vehicle Identification Number</span>' +
          '<span class="tupp-inline-configurator__manual-vin-row"><input data-tupp-manual-vin name="properties[VIN]" value="' + escapeAttribute(vin) + '" maxlength="17" inputmode="text" autocomplete="off" placeholder="e.g. 18UYA31581L000000"></span></label>' +
        '<p class="tupp-inline-configurator__helper">Enter all 17 characters. Your selected paint code stays in place, and the VIN is automatically sent with the order so our paint team can refine the color match.</p>' +
        (state.manualVinSaved ? '<p class="tupp-inline-configurator__manual-vin-success" role="status">VIN added to this color match.</p>' : '') +
      '</div>';
    }

    function packageImage(name, entry) {
      /* Prefer the mapped Shopify kit product so every tier shows its own art. */
      var source = (entry && entry.productImage) || packageImages[normalize(name)] || '';
      if (!source) return '<span class="tupp-package-choice__media" aria-hidden="true"></span>';
      return '<span class="tupp-package-choice__media"><img src="' + escapeAttribute(source) + '" alt="" loading="lazy"></span>';
    }

    function packageCard(name, entry, disabled) {
      var meta = packageMeta(name);
      var selected = !disabled && name === state.selectedPackage;
      var recommended = normalize(name) === 'essential';
      var attributes = disabled
        ? ' disabled aria-disabled="true"'
        : ' data-tupp-package="' + escapeAttribute(name) + '" aria-pressed="' + String(selected) + '"';

      return '<button type="button" class="tupp-package-choice' + (selected ? ' is-selected' : '') + '"' + attributes + '>' +
        packageImage(name, entry) +
        '<span class="tupp-package-choice__copy">' +
          '<span class="tupp-package-choice__topline"><span>' + escapeHtml(name) + '</span>' +
            (recommended ? '<span class="tupp-package-choice__recommendation">Recommended</span>' : '') +
          '</span>' +
          '<span class="tupp-package-choice__description">' + escapeHtml(meta.description) + '</span>' +
          '<span class="tupp-package-choice__includes">' + meta.inclusions.map(function (item) {
            return '<span>' + escapeHtml(item) + '</span>';
          }).join('') + '</span>' +
          '<span class="tupp-package-choice__footer"><span>' + escapeHtml(meta.note) + '</span>' +
            (!disabled ? '<strong>' + escapeHtml(formatMoney(entry && entry.price)) + '</strong>' : '') +
          '</span>' +
        '</span>' +
      '</button>';
    }

    function renderPackageCards(colorReady) {
      if (!colorReady || state.method === 'plate') {
        return '<div class="tupp-inline-configurator__packages" aria-label="Pen packages available after color matching">' +
          ['Essential', 'Preferred', 'Paint Only'].map(function (name) {
            return packageCard(name, null, true);
          }).join('') +
        '</div><p class="tupp-inline-configurator__helper">Match your vehicle or select a paint code to unlock pen package pricing.</p>';
      }

      var packages = orderedPackages();
      if (!packages.length) {
        return '<p class="tupp-inline-configurator__empty">No pen package is mapped to this color yet. Try VIN matching or contact a paint specialist.</p>';
      }
      state.selectedPackage = selectedPackage();
      return '<div class="tupp-inline-configurator__packages">' + packages.map(function (name) {
        return packageCard(name, entryForPackage(name), false);
      }).join('') + '</div>';
    }

    function selectionText(ready, packageReady) {
      if (!ready || !packageReady) return 'Complete your pen setup';
      var color = state.method === 'manual' ? colorLabel(state.colorCode) : 'Factory match';
      return color + ' - ' + state.selectedPackage + ' pen set';
    }

    function render() {
      var previousPreview = content.querySelector('[data-touchup-color-preview]');
      var ready = methodReady();
      var toneReady = toneSelectionReady();
      var packageName = ready && state.method !== 'plate' ? selectedPackage() : '';
      var packageReady = !!packageName && !!entryForPackage(packageName);
      if (packageReady) state.selectedPackage = packageName;

      content.innerHTML =
        garageMarkup(activeVehicle()) +
        '<div class="tupp-inline-configurator__step">' +
          '<div class="tupp-inline-configurator__step-heading"><span>01</span><div><h3>Find your factory color</h3><p>VIN matching gives the strongest factory-color path.</p></div></div>' +
          renderToneFields() +
          '<div class="tupp-inline-configurator__methods" role="radiogroup" aria-label="Choose a color matching method">' +
            ['vin', 'plate', 'manual'].map(function (method) {
              var labels = { vin: ['VIN match', 'Most accurate'], plate: ['License plate', 'VIN lookup'], manual: ['Paint code', 'I know it'] };
              var disabled = !toneReady || (secondaryToneSelected() && method !== 'manual');
              return '<button type="button" class="tupp-match-method' + (state.method === method ? ' is-selected' : '') + '" data-tupp-method="' + method + '" role="radio" aria-checked="' + String(state.method === method) + '"' + (disabled ? ' disabled' : '') + '><strong>' + labels[method][0] + '</strong><span>' + labels[method][1] + '</span></button>';
            }).join('') +
          '</div>' +
          (toneReady ? renderMethodFields() : '<p class="tupp-inline-configurator__helper">Choose the factory-color setup above to continue.</p>') +
          vinResultMarkup() +
          (state.method === 'vin' && state.plateMessage ? '<p class="tupp-inline-configurator__status" role="status">' + escapeHtml(state.plateMessage) + '</p>' : '') +
          (vinAvailabilityStatus() ? '<p class="tupp-inline-configurator__status" role="status">' + escapeHtml(vinAvailabilityStatus()) + '</p>' : '') +
        '</div>' +
        '<div class="tupp-inline-configurator__step' + (ready && state.method !== 'plate' ? '' : ' is-disabled') + '">' +
          '<div class="tupp-inline-configurator__step-heading"><span>02</span><div><h3>Choose your pen package</h3><p>Use the matched color pen for pinpoint control. Add clearcoat for protection, and primer only where the chip exposes primer or bare metal.</p></div></div>' +
          renderPackageCards(ready) +
        '</div>' +
        '<div class="tupp-inline-configurator__cta-row">' +
          '<div><span class="tupp-inline-configurator__selection-label">Your selection</span><strong>' + escapeHtml(selectionText(ready, packageReady)) + '</strong></div>' +
          '<button type="button" class="tupp-inline-configurator__atc" data-tupp-add' + ((ready && (state.method === 'plate' || packageReady) && !state.resolvingPlate && !state.adding) ? '' : ' disabled') + '>' +
            (state.adding ? 'Adding&hellip;' : state.resolvingPlate ? 'Finding your color&hellip;' : state.method === 'plate' ? 'Find my factory color' : 'Add to cart') + ' <span aria-hidden="true">&rarr;</span>' +
          '</button>' +
        '</div>';

      var colors = window.PartifyTouchupColorDetails;
      var preview = content.querySelector('[data-touchup-color-preview]');
      if (previousPreview && preview) {
        preview.replaceWith(previousPreview);
        preview = previousPreview;
      }
      if (colors) colors.renderPreview(preview, state.colorDetails, state.colorCode);
      bind();
    }

    function scheduleRender() {
      window.clearTimeout(renderTimer);
      renderTimer = window.setTimeout(render, 0);
    }

    function syncVinToGarage() {
      var vin = state.vin;
      if (vin.length !== 17) return Promise.resolve(false);
      if (state.resolvedVin === vin) return Promise.resolve(true);
      var sync = window.PartifyTouchupVinGarageSync;
      if (!sync || typeof sync.decodeAndActivate !== 'function') {
        state.resolvedVin = vin;
        state.resolvingVin = false;
        state.plateMessage = '';
        scheduleRender();
        return Promise.resolve(true);
      }
      state.resolvingVin = true;
      state.plateMessage = 'Decoding VIN and updating your Garage\u2026';
      render();
      return Promise.resolve(sync.decodeAndActivate(vin)).then(function (vehicle) {
        if (state.vin !== vin) return false;
        state.resolvingVin = false;
        state.resolvedVin = vin;
        state.plateMessage = vinDecodeSuccessMessage(vehicle);
        return loadVehiclePaintContext(vehicle || activeVehicle()).then(function () {
          if (state.vin !== vin) return false;
          scheduleRender();
          return true;
        });
      }).catch(function () {
        if (state.vin !== vin) return false;
        state.resolvingVin = false;
        state.resolvedVin = '';
        state.plateMessage = 'We could not decode that VIN. Check it and try again.';
        scheduleRender();
        return false;
      });
    }

    function resolvePlate() {
      if (state.resolvingPlate) return;
      if (typeof window.fetchBumpersLicenseToVin !== 'function') {
        state.plateMessage = 'Plate lookup is still loading. Try again, or use your VIN or paint code.';
        scheduleRender();
        return;
      }

      state.resolvingPlate = true;
      state.plateMessage = '';
      render();
      Promise.resolve(window.fetchBumpersLicenseToVin(state.plateState, state.plate))
        .then(function (result) {
          var vin = normalizeVin(result && result.vin);
          if (vin.length !== 17) throw new Error('No VIN found');
          state.method = 'vin';
          state.vin = vin;
          state.resolvedVin = '';
          state.userChoseMethod = true;
          state.colorCode = '';
          state.selectedPackage = '';
          state.plateMessage = '';
          state.resolvingPlate = false;
          syncVinToGarage();
        })
        .catch(function () {
          state.resolvingPlate = false;
          state.plateMessage = 'We could not resolve that plate. Check it, or use your VIN or paint code.';
          scheduleRender();
        });
    }

    function cartProperties(form, entry) {
      var properties = {};
      if (form) {
        new FormData(form).forEach(function (value, key) {
          var match = key.match(/^properties\[(.+)\]$/);
          if (match && value != null && String(value).length) properties[match[1]] = value;
        });
      }
      var resolvedCode = codeForMethod();
      properties['Touch-Up Color'] = resolvedCode && !isVinMatch(resolvedCode) ? resolvedCode : 'Match by VIN';
      properties['Kit Format'] = entry.size || 'Pen';
      properties['Kit Package'] = entry.packageName || selectedPackage();
      properties['Kit Product'] = entry.productTitle || document.querySelector('h1')?.textContent?.trim() || document.title;
      var activeVehicleLabel = vehicleLabel(activeVehicle());
      if (activeVehicleLabel) properties.Vehicle = activeVehicleLabel;
      else delete properties.Vehicle;
      delete properties.EasySearch;
      delete properties.Fits;
      var activeVin = activeVinString();
      if (activeVin.length === 17) properties.VIN = activeVin;
      else delete properties.VIN;
      return properties;
    }

    function vehicleIdentity(vehicle) {
      if (!vehicle) return '';
      return normalize([
        vehicle.year, vehicle.make, vehicle.model, vehicle.submodel || vehicle.trim,
        vehicle.engine, vehicle.vin, vehicle.plate,
      ].filter(Boolean).join(' ') || vehicle.ymm || vehicle.YMM || vehicle.vehicle);
    }

    function confirmQuantity(payload, properties) {
      var confirmation = window.PartifyCartQuantityConfirmation;
      if (!confirmation || typeof confirmation.confirmAdd !== 'function') return Promise.resolve(true);
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : { items: [] }; })
        .catch(function () { return { items: [] }; })
        .then(function (cart) {
          return confirmation.confirmAdd({
            cart: cart,
            items: [{ id: payload.id, variant_id: payload.id, quantity: 1, title: properties['Kit Product'], properties: properties, partifyQuantityRuleKey: 'touch_up_paint_kit' }],
          }).then(function (result) { return result.confirmed !== false; });
        });
    }

    function submitNativeForm() {
      if (state.adding) return;
      if (state.method === 'vin' && (state.allowedCodesLoading || !vinPaintDataAvailable())) {
        state.plateMessage = vinAvailabilityStatus() || 'We could not verify Shopify paint data for this vehicle. Match by VIN and Add to Cart are disabled.';
        scheduleRender();
        return;
      }
      var entry = entryForPackage(selectedPackage());
      if (!entry || !entry.id) return;
      state.adding = true;
      if (!syncExistingControls()) {
        state.adding = false;
        return;
      }
      var form = document.getElementById('product-form') || document.querySelector('form.product-form, form[action*="/cart/add"]');
      var properties = cartProperties(form, entry);
      var payload = { id: Number(entry.id), quantity: 1, properties: properties };
      var button = content.querySelector('[data-tupp-add]');
      var idleLabel = button ? button.innerHTML : '';
      if (button) { button.disabled = true; button.textContent = 'Adding\u2026'; }

      confirmQuantity(payload, properties)
        .then(function (confirmed) {
          if (!confirmed) {
            state.adding = false;
            scheduleRender();
            return null;
          }
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });
        })
        .then(function (response) {
          if (response === null) return;
          if (!response.ok) throw new Error('Unable to add this pen package to the cart.');
          if (window.__partifyAddOnStyles?.afterAdd({ items: [payload], onClose: function () { state.adding = false; scheduleRender(); } })) return;
          window.location.assign('/cart');
        })
        .catch(function (error) {
          state.adding = false;
          state.plateMessage = error.message || 'We could not add this package. Please try again.';
          if (button) { button.disabled = false; button.innerHTML = idleLabel; }
          window.alert(state.plateMessage);
        });
    }

    function addToCart() {
      if (state.method === 'plate') {
        resolvePlate();
        return;
      }
      if (state.method === 'vin' && state.resolvedVin !== state.vin) {
        syncVinToGarage().then(function (ready) { if (ready) addToCart(); });
        return;
      }
      submitNativeForm();
    }

    function bind() {
      Array.prototype.slice.call(content.querySelectorAll('[data-tupp-method]')).forEach(function (button) {
        button.addEventListener('click', function () {
          setMethod(button.getAttribute('data-tupp-method'));
        });
      });

      Array.prototype.slice.call(content.querySelectorAll('[data-tupp-tone]')).forEach(function (button) {
        button.addEventListener('click', function () {
          state.toneChoice = button.getAttribute('data-tupp-tone') || '';
          state.toneArea = '';
          state.colorCode = '';
          state.selectedPackage = '';
          scheduleRender();
        });
      });

      Array.prototype.slice.call(content.querySelectorAll('[data-tupp-tone-area]')).forEach(function (button) {
        button.addEventListener('click', function () {
          state.toneArea = button.getAttribute('data-tupp-tone-area') || '';
          state.colorCode = '';
          state.selectedPackage = '';
          if (state.toneArea === 'secondary') setMethod('manual');
          else scheduleRender();
        });
      });

      Array.prototype.slice.call(content.querySelectorAll('[data-tupp-package]')).forEach(function (button) {
        button.addEventListener('click', function () {
          state.selectedPackage = button.getAttribute('data-tupp-package') || '';
          syncExistingControls();
          scheduleRender();
        });
      });

      var color = content.querySelector('[data-tupp-color]');
      if (color) color.addEventListener('change', function () {
        state.userChoseMethod = true;
        state.colorCode = color.value;
        state.manualVinSaved = false;
        window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = true;
        window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = false;
        state.selectedPackage = '';
        scheduleRender();
      });

      var manualVin = content.querySelector('[data-tupp-manual-vin]');
      if (manualVin) manualVin.addEventListener('input', function () {
        state.vin = normalizeVin(manualVin.value);
        state.manualVinSaved = false;
        manualVin.value = state.vin;
        var submit = content.querySelector('[data-tupp-manual-vin-submit]');
        var prompt = content.querySelector('[data-tupp-manual-vin-prompt]');
        var success = prompt && prompt.querySelector('.tupp-inline-configurator__manual-vin-success');
        if (state.vin.length === 17) {
          state.manualVinSaved = true;
          syncManualVinProperty();
          if (submit) { submit.textContent = 'VIN added'; submit.disabled = true; }
          if (prompt && !success) prompt.insertAdjacentHTML('beforeend', '<p class="tupp-inline-configurator__manual-vin-success" role="status">VIN added to this color match.</p>');
        } else {
          if (submit) { submit.textContent = 'Add VIN'; submit.disabled = true; }
          if (success) success.remove();
        }
      });

      var vin = content.querySelector('[data-tupp-vin]');
      if (vin) vin.addEventListener('input', function () {
        var wasReady = state.vin.length === 17;
        state.userChoseMethod = true;
        state.vin = normalizeVin(vin.value);
        state.resolvedVin = '';
        state.plateMessage = '';
        window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = false;
        vin.value = state.vin;
        state.selectedPackage = '';
        if (state.vin.length === 17) syncVinToGarage();
        else if (wasReady) scheduleRender();
      });

      var plate = content.querySelector('[data-tupp-plate]');
      if (plate) plate.addEventListener('input', function () {
        state.plate = normalizePlate(plate.value);
        plate.value = state.plate;
        state.plateMessage = '';
        var addButton = content.querySelector('[data-tupp-add]');
        if (addButton && state.method === 'plate') {
          addButton.disabled = state.plate.length < 2 || !state.plateState || state.resolvingPlate;
        }
      });

      var plateState = content.querySelector('[data-tupp-plate-state]');
      if (plateState) plateState.addEventListener('change', function () {
        state.plateState = plateState.value;
        state.plateMessage = '';
        scheduleRender();
      });

      var add = content.querySelector('[data-tupp-add]');
      if (add) add.addEventListener('click', addToCart);

      Array.prototype.slice.call(content.querySelectorAll('[data-tupp-garage]')).forEach(function (button) {
        button.addEventListener('click', openGarage);
      });
    }

    function hydrateVehicle(force) {
      var vehicle = activeVehicle();
      if (!vehicle) return false;
      var nextKey = vehicleIdentity(vehicle);
      var changed = nextKey !== state.vehicleKey;
      if (state.userChoseMethod && !force && !changed) return false;
      state.vehicleKey = nextKey;
      if (changed) state.userChoseMethod = false;
      var vin = normalizeVin(vehicle.vin);
      var paintCode = String(vehicle.paintCode || vehicle.paint_code || '').trim();
      if (vin.length === 17) {
        state.method = 'vin';
        state.vin = vin;
        state.resolvedVin = vin;
        state.colorCode = '';
      } else if (paintCode && mapKeyForCode(paintCode)) {
        state.method = 'manual';
        state.colorCode = mapKeyForCode(paintCode);
      }
      return changed;
    }

    function refreshFromGarage() {
      var vehicle = activeVehicle();
      if (hydrateVehicle(false)) {
        state.selectedPackage = '';
        state.toneChoice = '';
        state.toneArea = '';
      }
      loadVehiclePaintContext(vehicle);
      scheduleRender();
    }

    function waitForData(attempt) {
      window.clearTimeout(dataRetryTimer);
      if (Object.keys(getMap()).length) {
        hydrateVehicle();
        render();
        loadVehiclePaintContext(activeVehicle());
        return;
      }
      if (attempt >= DATA_RETRY_LIMIT) {
        content.innerHTML = '<p class="tupp-inline-configurator__empty">Factory-color options are still loading. Refresh the page, then try again.</p>';
        return;
      }
      dataRetryTimer = window.setTimeout(function () { waitForData(attempt + 1); }, DATA_RETRY_DELAY);
    }

    window.addEventListener('partify:vehicle:changed', refreshFromGarage);
    window.addEventListener('searchTermsUpdated', refreshFromGarage);
    document.addEventListener('partify:vehicle:changed', refreshFromGarage);
    document.addEventListener('partify:touchup:selection-complete', scheduleRender);
    document.addEventListener('variantsUpdated', function () {
      window.setTimeout(function () { waitForData(0); }, 0);
    });

    waitForData(0);
  }

  function bootAll() {
    Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR)).forEach(boot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();

  document.addEventListener('shopify:section:load', bootAll);
})();
